import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ApiError, fetchFastApi } from "../api/fastapi";
import {
  fetchSSE,
  fetchSSEWithResume,
  ResumeRequest,
  SSEResultData,
  ToolEvent,
  AgentCall,
  AgentEvent,
  DraftingProgress,
} from "../api/sseClient";
export type { ToolEvent, AgentCall, AgentEvent };
import { toast, type ExternalToast } from "sonner";
import { usePresignedUpload } from "./usePresignedUpload";
import type { DraftPendingColumnsCallbacks } from "./useDraftPendingColumnsOnOpen";
import {
  CollabWorkStepDocumentUploadResponseSchema,
  CollabWorkStepSessionDetail,
  CollabWorkStepSessionDetailSchema,
  WorkStepEditedSinceSessionStart,
  WorkStepEditedSinceSessionStartSchema,
  WorkStepDocumentInfo,
  WorkStepDocumentInfoSchema,
  CreateCollabWorkStepSessionResponseSchema,
  WorkStepDraftPlan,
  parseWorkStepPlan,
  WorkStepPlanPatch,
  planPatchToRequestBody,
} from "../models/audit/collabWorkStepSession";

/**
 * Fork of useCollabControl.ts for the work-step collaborative AI generation
 * flow, targeting the `/work-steps/collab/...` router. Same stream topology:
 * planning and messaging re-attach to their run through
 * `runs/{run_id}/stream` if the connection dies, generation and plan patches
 * stream once — and the same silent-stream nuance on patch-only updatePlan
 * calls (see useCollabWorkStepSSE.updatePlan below) — see that file's module
 * comment for why request bodies stay snake_case while response payloads
 * here parse as camelCase.
 */

/**
 * Hook for creating a new collaborative work-step session
 */
export const useCreateCollabWorkStepSession = () => {
  const [loading, setLoading] = useState(false);
  const {
    state: presignedState,
    uploadFiles,
    cancel: cancelUpload,
  } = usePresignedUpload();

  const createSession = useCallback(
    async (params: {
      files?: File[];
      externalRef?: string;
    }): Promise<string | null> => {
      setLoading(true);
      try {
        let documentIds: string[] = [];

        // Step 1: Upload files if provided
        const allFiles = params.files || [];

        if (allFiles.length > 0) {
          // Use presigned upload for large files to avoid 413 errors
          const uploadResult = await uploadFiles(allFiles);

          const formData = new FormData();
          if (uploadResult.usedPresigned) {
            formData.append("upload_ids", uploadResult.uploadIds.join(","));
          } else {
            uploadResult.files.forEach((file) => {
              formData.append("files", file);
            });
          }

          const uploadResponse = await fetchFastApi({
            path: "/work-steps/collab/documents/upload",
            method: "POST",
            body: formData,
          });

          const uploadParsed =
            CollabWorkStepDocumentUploadResponseSchema.parse(uploadResponse);
          documentIds = uploadParsed.documents.map((doc) => doc.id);
        }

        if (documentIds.length === 0 && !params.externalRef) {
          toast.error("No documents were uploaded");
          return null;
        }

        // Step 2: Create the collab session
        const body: Record<string, string | number | object | boolean | null> =
          {
            document_ids: documentIds,
          };
        if (params.externalRef) {
          body.external_ref = params.externalRef;
        }

        const response = await fetchFastApi({
          path: "/work-steps/collab/sessions",
          method: "POST",
          body,
        });

        const parsed =
          CreateCollabWorkStepSessionResponseSchema.parse(response);
        return parsed.sessionId;
      } catch (error) {
        console.error("Failed to create collab work-step session:", error);
        toast.error("Failed to create collaborative session");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [uploadFiles],
  );

  return {
    createSession,
    loading,
    presignedUploadState: presignedState,
    cancelUpload,
  };
};

/**
 * The documents attached to a generation session (its `work_step_document` rows
 * keyed by `generation_session_id`), plus attach and remove operations. Backs
 * the Context Documents panel during initial generation, before the docs are
 * reassigned onto the work step at generation time.
 *
 * Attach is two steps here (upload, then link the returned ids) because the
 * session endpoint takes document ids, not multipart.
 */
export const useCollabWorkStepSessionDocuments = (sessionId: string | null) => {
  const [documents, setDocuments] = useState<WorkStepDocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const { uploadFiles } = usePresignedUpload();

  const fetchDocuments = useCallback(async () => {
    if (!sessionId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/work-steps/collab/sessions/${sessionId}/documents`,
        method: "GET",
      });
      setDocuments(WorkStepDocumentInfoSchema.array().parse(response));
    } catch (error) {
      console.error("Failed to fetch session documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  const attachDocuments = useCallback(
    async (files: File[]) => {
      if (!sessionId || files.length === 0) return;
      setMutating(true);
      try {
        const uploadResult = await uploadFiles(files);

        const formData = new FormData();
        if (uploadResult.usedPresigned) {
          formData.append("upload_ids", uploadResult.uploadIds.join(","));
        } else {
          uploadResult.files.forEach((file) => formData.append("files", file));
        }

        const uploadResponse = await fetchFastApi({
          path: "/work-steps/collab/documents/upload",
          method: "POST",
          body: formData,
        });
        const documentIds = CollabWorkStepDocumentUploadResponseSchema.parse(
          uploadResponse,
        ).documents.map((doc) => doc.id);

        await fetchFastApi({
          path: `/work-steps/collab/sessions/${sessionId}/documents`,
          method: "POST",
          body: { document_ids: documentIds },
        });
        // Refetch: the list is the source of truth for the junction ids and parse states.
        await fetchDocuments();
      } catch (error) {
        console.error("Failed to attach session documents:", error);
        toast.error("Failed to upload documents");
      } finally {
        setMutating(false);
      }
    },
    [sessionId, uploadFiles, fetchDocuments],
  );

  const removeDocument = useCallback(
    async (workStepDocumentId: string) => {
      if (!sessionId) return;
      // Optimistic; refetch on failure restores the true set.
      setDocuments((prev) =>
        prev.filter((doc) => doc.workStepDocumentId !== workStepDocumentId),
      );
      try {
        await fetchFastApi({
          path: `/work-steps/collab/sessions/${sessionId}/documents/${workStepDocumentId}`,
          method: "DELETE",
        });
      } catch (error) {
        console.error("Failed to remove session document:", error);
        toast.error("Failed to remove document");
        await fetchDocuments();
      }
    },
    [sessionId, fetchDocuments],
  );

  return {
    documents,
    loading,
    mutating,
    refetch: fetchDocuments,
    attachDocuments,
    removeDocument,
  };
};

/**
 * Hook for opening a "Revise with AI" session seeded from an existing work step.
 * The backend converts the work step to a draft plan and returns a session
 * already in the iterating phase, ready for the conversational feedback loop.
 */
export const useCreateCollabSessionFromWorkStep = () => {
  const [loading, setLoading] = useState(false);

  const createFromWorkStep = useCallback(
    async (
      workStepId: string,
      documentIds: string[] = [],
    ): Promise<string | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: "/work-steps/collab/sessions/from-work-step",
          method: "POST",
          body: { work_step_id: workStepId, document_ids: documentIds },
        });
        const parsed =
          CreateCollabWorkStepSessionResponseSchema.parse(response);
        return parsed.sessionId;
      } catch (error) {
        console.error("Failed to create revise session:", error);
        toast.error("Failed to start revise session");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createFromWorkStep, loading };
};

// Mirrors FinalizeReviseResponse on the backend; the skip reason is why a
// launch-parked Optro test was NOT created at finalize (null = created or
// nothing pending). droppedAttributeLinks holds one reason per drafted
// attribute link that was not applied because another kind of column already
// answers that attribute.
// Each field falls back on its own (.catch), so a malformed value in one never
// discards the other.
const FinalizeReviseWorkStepResponseSchema = z.object({
  pendingTestSkipped: z
    .string()
    .nullish()
    .transform((value) => value ?? null)
    .catch(null),
  droppedAttributeLinks: z
    .array(z.string())
    .nullish()
    .transform((value) => value ?? [])
    .catch([]),
});

export type FinalizeReviseOutcome =
  | {
      kind: "finalized";
      workStepId: string;
      pendingTestSkipped: string | null;
      droppedAttributeLinks: string[];
    }
  | { kind: "editedSinceStart"; conflict: WorkStepEditedSinceSessionStart };

/**
 * Hook for finalizing a "Revise with AI" session: persists the session's current
 * plan back onto its source work step (ID-stable, in place). A finalized outcome
 * carries any pending-test skip reason and any attribute links that were not
 * applied.
 *
 * If the work step was edited since the session began, the first finalize is
 * refused with what it would overwrite ("editedSinceStart"); calling again with
 * `confirmOverwrite` applies the plan anyway. Returns null on any other failure.
 */
export const useFinalizeReviseWorkStepSession = () => {
  const [finalizing, setFinalizing] = useState(false);

  const finalizeRevise = useCallback(
    async (
      sessionId: string,
      workStepId: string,
      confirmOverwrite = false,
    ): Promise<FinalizeReviseOutcome | null> => {
      setFinalizing(true);
      try {
        const response = await fetchFastApi({
          path: `/work-steps/collab/sessions/${sessionId}/finalize`,
          method: "POST",
          body: {
            work_step_id: workStepId,
            confirm_overwrite: confirmOverwrite,
          },
        });
        const parsed = FinalizeReviseWorkStepResponseSchema.safeParse(response);
        return {
          kind: "finalized",
          workStepId,
          pendingTestSkipped: parsed.success
            ? parsed.data.pendingTestSkipped
            : null,
          droppedAttributeLinks: parsed.success
            ? parsed.data.droppedAttributeLinks
            : [],
        };
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          const conflict = WorkStepEditedSinceSessionStartSchema.safeParse(
            error.detailData,
          );
          if (conflict.success) {
            return { kind: "editedSinceStart", conflict: conflict.data };
          }
        }
        console.error("Failed to finalize revise session:", error);
        toast.error("Failed to save changes to the work step");
        return null;
      } finally {
        setFinalizing(false);
      }
    },
    [],
  );

  return { finalizeRevise, finalizing };
};

/**
 * Hook for fetching collaborative session details
 */
export const useCollabWorkStepSessionDetail = (sessionId: string | null) => {
  const [session, setSession] = useState<CollabWorkStepSessionDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSession = useCallback(
    async (options?: { backgroundFetch?: boolean }) => {
      if (!sessionId) {
        setSession(null);
        setLoading(false);
        return;
      }

      if (!options?.backgroundFetch) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await fetchFastApi({
          path: `/work-steps/collab/sessions/${sessionId}/detail`,
          method: "GET",
        });

        const parsed = CollabWorkStepSessionDetailSchema.parse(response);
        setSession(parsed);
      } catch (err) {
        console.error("Failed to fetch collab work-step session:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch session";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  return {
    session,
    loading,
    error,
    refetch: fetchSession,
    setSession,
  };
};

/**
 * SSE streaming state
 */
interface SSEStreamState {
  isStreaming: boolean;
  /** True while the in-flight stream is a silent plan patch (a direct edit or
   * restore with no feedback): the server applies it without an agent turn, so
   * the chat should not render a pending bubble for it. */
  streamingSilent: boolean;
  streamingContent: string;
  error: string | null;
  heartbeatCounter: number;
  lastThinkingAt: number;
}

interface PlanUpdateCallbacks {
  onChunk?: (content: string) => void;
  onPlanUpdated?: (plan: WorkStepDraftPlan) => void;
  onComplete?: (assistantMessage: string) => void;
  onError?: (error: string) => void;
}

/**
 * Shared SSE callbacks wired into every fetchSSE call.
 * Appends typed events to the agentEvents timeline in arrival order.
 * Heartbeats are not display events — they update the liveness state instead.
 */
function makeAgentEventCallbacks(
  setAgentEvents: React.Dispatch<React.SetStateAction<AgentEvent[]>>,
  setState: React.Dispatch<React.SetStateAction<SSEStreamState>>,
) {
  const append = (event: AgentEvent) =>
    setAgentEvents((prev) => [...prev, event].slice(-40));

  return {
    onToolCall: (toolEvent: ToolEvent) => {
      append({ type: "tool_call", toolEvent });
    },
    onToolResult: (toolEvent: ToolEvent) => {
      append({ type: "tool_result", toolEvent });
    },
    onAgentCall: (agentCall: AgentCall) => {
      append({ type: "agent_call", agentCall });
    },
    onHeartbeat: (count: number) => {
      setState((prev) => ({
        ...prev,
        heartbeatCounter: count,
        lastThinkingAt: Date.now(),
      }));
    },
    // Drafting counts as liveness: it runs after the agent's last word, or
    // with no agent turn at all. Every frame, even an end that names no
    // column, counts as liveness and restarts the stall window.
    onDraftingProgress: (drafting: DraftingProgress) => {
      append({ type: "drafting_progress", drafting });
      setState((prev) => ({ ...prev, lastThinkingAt: Date.now() }));
    },
  } as const;
}

/**
 * Hook for SSE operations (planning, messaging, generating) against the
 * work-step collab session.
 */
export const useCollabWorkStepSSE = (sessionId: string | null) => {
  const [state, setState] = useState<SSEStreamState>({
    isStreaming: false,
    streamingSilent: false,
    streamingContent: "",
    error: null,
    heartbeatCounter: 0,
    lastThinkingAt: 0,
  });
  const [agentEvents, setAgentEvents] = useState<AgentEvent[]>([]);
  const [isStalled, setIsStalled] = useState(false);
  // Non-zero while a lost stream is being re-attached to its run. The work is
  // still going; only the connection to it broke.
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isStreaming: false,
    }));
  }, []);

  /** Reset all stream state and begin a new AbortController. */
  const beginStream = useCallback(
    (options?: { silent?: boolean }): AbortController => {
      cancel();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setAgentEvents([]);
      setIsStalled(false);
      setReconnectAttempt(0);
      setState({
        isStreaming: true,
        streamingSilent: options?.silent ?? false,
        streamingContent: "",
        error: null,
        heartbeatCounter: 0,
        lastThinkingAt: 0,
      });
      return abortController;
    },
    [cancel],
  );

  // onChunk/onError/onComplete are identical across all four SSE calls below;
  // only what onComplete hands back to the caller and how the error toast
  // renders differ.
  const makeStreamCallbacks = useCallback(
    (
      callbacks: {
        onChunk?: (content: string) => void;
        onError?: (error: string, status?: number) => void;
      },
      notifyComplete: () => void,
      errorToastOptions?: ExternalToast,
    ) => ({
      onChunk: (content: string, isFinal: boolean) => {
        setState((prev) => ({
          ...prev,
          streamingContent: prev.streamingContent + content,
        }));
        callbacks.onChunk?.(content);
        if (isFinal) {
          setState((prev) => ({ ...prev, streamingContent: "" }));
        }
      },
      onError: (error: string, status?: number) => {
        setState((prev) => ({ ...prev, isStreaming: false, error }));
        setReconnectAttempt(0);
        callbacks.onError?.(error, status);
        toast.error(error, errorToastOptions);
      },
      onComplete: () => {
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          streamingContent: "",
        }));
        setReconnectAttempt(0);
        notifyComplete();
      },
    }),
    [],
  );

  const startPlanning = useCallback(
    async (callbacks: {
      onChunk?: (content: string) => void;
      onPlanReady?: (plan: WorkStepDraftPlan) => void;
      onComplete?: () => void;
      onError?: (error: string) => void;
    }): Promise<void> => {
      if (!sessionId) return;

      const abortController = beginStream();
      const streamCallbacks = makeStreamCallbacks(callbacks, () =>
        callbacks.onComplete?.(),
      );

      await fetchSSEWithResume(
        `/work-steps/collab/sessions/${sessionId}/plan`,
        { method: "POST" },
        // Without a run id — the connection died before the stream named it —
        // ask to plan again. Planning is one operation per session, and the API
        // answers a second request by attaching to the run already doing it.
        (runId): ResumeRequest =>
          runId
            ? {
                path: `/work-steps/collab/sessions/${sessionId}/runs/${runId}/stream`,
                options: { method: "GET" },
              }
            : {
                path: `/work-steps/collab/sessions/${sessionId}/plan`,
                options: { method: "POST" },
              },
        {
          ...makeAgentEventCallbacks(setAgentEvents, setState),
          ...streamCallbacks,
          onReconnecting: setReconnectAttempt,
          onResult: (result: SSEResultData) => {
            if (result.plan) {
              try {
                const plan = parseWorkStepPlan(result.plan, result);
                callbacks.onPlanReady?.(plan);
              } catch (e) {
                console.error("Failed to parse plan:", e);
              }
            }
          },
        },
        abortController.signal,
      );
    },
    [sessionId, beginStream, makeStreamCallbacks],
  );

  const sendMessage = useCallback(
    async (
      message: string,
      callbacks: {
        onChunk?: (content: string) => void;
        onPlanUpdated?: (plan: WorkStepDraftPlan) => void;
        onComplete?: (assistantMessage: string) => void;
        onError?: (error: string) => void;
      },
    ): Promise<void> => {
      if (!sessionId) return;

      const abortController = beginStream();
      let fullResponse = "";
      const streamCallbacks = makeStreamCallbacks(
        {
          ...callbacks,
          onChunk: (content) => {
            fullResponse += content;
            callbacks.onChunk?.(content);
          },
        },
        () => callbacks.onComplete?.(fullResponse),
      );

      await fetchSSEWithResume(
        `/work-steps/collab/sessions/${sessionId}/message`,
        { method: "POST", body: { message } },
        // Only ever resumes by name. Re-sending the message would ask the same
        // question twice, so a connection that died before the stream named its
        // run gives up rather than guess.
        (runId): ResumeRequest =>
          runId
            ? {
                path: `/work-steps/collab/sessions/${sessionId}/runs/${runId}/stream`,
                options: { method: "GET" },
              }
            : null,
        {
          ...makeAgentEventCallbacks(setAgentEvents, setState),
          ...streamCallbacks,
          onReconnecting: setReconnectAttempt,
          onResult: (result: SSEResultData) => {
            if (result.plan) {
              try {
                const plan = parseWorkStepPlan(result.plan, result);
                callbacks.onPlanUpdated?.(plan);
              } catch (e) {
                console.error("Failed to parse updated plan:", e);
              }
            }
          },
        },
        abortController.signal,
      );
    },
    [sessionId, beginStream, makeStreamCallbacks],
  );

  const generateWorkStep = useCallback(
    async (callbacks: {
      onChunk?: (content: string) => void;
      onWorkStepCreated?: (workStepId: string) => void;
      onPendingTestSkipped?: (reason: string) => void;
      onOptroFieldsNotCarried?: () => void;
      onComplete?: () => void;
      onError?: (error: string, status?: number) => void;
    }): Promise<void> => {
      if (!sessionId) return;

      const abortController = beginStream();

      const streamCallbacks = makeStreamCallbacks(
        callbacks,
        () => callbacks.onComplete?.(),
        // A refusal lists each contested attribute on its own line.
        { classNames: { title: "whitespace-pre-line" } },
      );

      await fetchSSE(
        `/work-steps/collab/sessions/${sessionId}/generate`,
        { method: "POST" },
        {
          ...makeAgentEventCallbacks(setAgentEvents, setState),
          ...streamCallbacks,
          onResult: (result: SSEResultData) => {
            if (result.workStepId) {
              callbacks.onWorkStepCreated?.(String(result.workStepId));
            }
            if (typeof result.pendingTestSkipped === "string") {
              callbacks.onPendingTestSkipped?.(result.pendingTestSkipped);
            }
            if (result.optroFieldsFailed === true) {
              callbacks.onOptroFieldsNotCarried?.();
            }
          },
        },
        abortController.signal,
      );
    },
    [sessionId, beginStream, makeStreamCallbacks],
  );

  // update-plan and draft-pending-columns share their events and result shape.
  // Without `onTransportError`, a dropped connection is reported through
  // `onError` and its toast.
  const streamPlanUpdate = useCallback(
    async (
      path: string,
      body: Record<string, unknown> | undefined,
      silent: boolean,
      callbacks: PlanUpdateCallbacks & {
        onTransportError?: (error: string) => void;
      },
    ): Promise<void> => {
      const abortController = beginStream({ silent });
      let fullResponse = "";
      const { onTransportError } = callbacks;
      const streamCallbacks = makeStreamCallbacks(
        {
          ...callbacks,
          onChunk: (content) => {
            fullResponse += content;
            callbacks.onChunk?.(content);
          },
        },
        () => callbacks.onComplete?.(fullResponse),
      );

      await fetchSSE(
        path,
        { method: "POST", body },
        {
          ...makeAgentEventCallbacks(setAgentEvents, setState),

          ...streamCallbacks,
          onTransportError:
            onTransportError &&
            ((error: string) => {
              setState((prev) => ({
                ...prev,
                isStreaming: false,
                streamingContent: "",
              }));
              onTransportError(error);
            }),
          onResult: (result: SSEResultData) => {
            if (result.plan) {
              try {
                const plan = parseWorkStepPlan(result.plan, result);
                callbacks.onPlanUpdated?.(plan);
              } catch (e) {
                console.error("Failed to parse updated plan:", e);
              }
            }
          },
        },
        abortController.signal,
      );
    },
    [beginStream, makeStreamCallbacks],
  );

  const updatePlan = useCallback(
    async (
      patch: WorkStepPlanPatch | null,
      feedback: string | null,
      feedbackIsCommentOnly: boolean,
      callbacks: PlanUpdateCallbacks,
    ): Promise<void> => {
      if (!sessionId) {
        return;
      }

      // A patch with no feedback is applied server-side without an agent turn
      // and streams nothing back — mark the stream silent so the chat panel
      // doesn't render a pending bubble for it.
      await streamPlanUpdate(
        `/work-steps/collab/sessions/${sessionId}/update-plan`,
        {
          // The backend PlanPatchRequest is snake_case throughout.
          patch: patch ? planPatchToRequestBody(patch) : null,
          feedback: feedback,
          feedback_is_comment_only: feedbackIsCommentOnly,
        },
        feedback === null,
        callbacks,
      );
    },
    [sessionId, streamPlanUpdate],
  );

  // Never silent: the turn's only visible output is its drafting progress.
  const draftPendingColumns = useCallback(
    async (
      callbacks: DraftPendingColumnsCallbacks<WorkStepDraftPlan>,
    ): Promise<void> => {
      if (!sessionId) {
        return;
      }
      await streamPlanUpdate(
        `/work-steps/collab/sessions/${sessionId}/draft-pending-columns`,
        undefined,
        false,
        callbacks,
      );
    },
    [sessionId, streamPlanUpdate],
  );

  const undo = useCallback(async (): Promise<{
    plan: WorkStepDraftPlan;
    canUndo: boolean;
    undoCount: number;
  } | null> => {
    if (!sessionId) return null;

    try {
      const response = await fetchFastApi({
        path: `/work-steps/collab/sessions/${sessionId}/undo`,
        method: "POST",
      });

      const plan = parseWorkStepPlan(response.plan, response);
      return {
        plan,
        canUndo: Boolean(response.canUndo),
        undoCount: Number(response.undoCount) || 0,
      };
    } catch (err) {
      // A zod failure here means the response shape drifted; don't dump raw
      // issues at the user — log the detail, show a plain message.
      console.error("Failed to undo:", err);
      toast.error("Couldn't undo the last change.");
      return null;
    }
  }, [sessionId]);

  // Watchdog: flag as stalled if no ThinkingChunk heartbeat for >8s while streaming.
  // Clears automatically when streaming stops or a new heartbeat arrives.
  useEffect(() => {
    if (!state.isStreaming || state.lastThinkingAt === 0) {
      setIsStalled(false);
      return;
    }
    const interval = setInterval(() => {
      setIsStalled(Date.now() - state.lastThinkingAt > 8000);
    }, 1000);
    return () => clearInterval(interval);
  }, [state.isStreaming, state.lastThinkingAt]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    ...state,
    agentEvents,
    isStalled,
    isReconnecting: reconnectAttempt > 0,
    startPlanning,
    sendMessage,
    generateWorkStep,
    updatePlan,
    draftPendingColumns,
    undo,
    cancel,
  };
};
